<template>
    <Head title="Liste des demandes d'envoi de colis" />

    <TravelLayout :currency="currency">
        <div x-data="handleSelect">
            <Datatables>
                <template #title>
                    Liste des demandes d'envoie de colis
                    <span class="font-medium">({{ requests.total }})</span>
                </template>
                <template #headerButtons>
                    <button class="rounded-md py-1 px-2 bg-white text-sky hover:bg-sky-400 hover:text-white"
                        @click="showSearchModal" title="Rechercher"
                        ><i  class="fad fa-search"></i>
                    </button>
                </template>
                <template #tableHeader>
                    <th>Code</th>
                    <th>Demandeur</th>
                    <th>Envoyée le</th>
                    <th># Colis</th>
                    <th>Statut</th>
                    <th>&nbsp;</th>
                </template>
                <template #tableBody>
                    <template v-if="!requests.data.length">
                        <tr>
                            <td colspan="6">
                                <div class="text-center text-2xl text-red-400 bg-red-100 p-10">
                                    Aucune demande d'envoie de colis.
                                </div>
                            </td>
                        </tr>
                    </template>
                    <template v-else v-for="(req, index) in requests.data" :key="index">
                        <tr>
                            <td>{{ req.code }}</td>
                            <td>
                                <div class="flex items-center">
                                    <div class="shrink-0 flex items-center justify-center rounded-full mr-2"
                                        :class="req.user ? 'bg-gray-700' : 'bg-orange-100'">

                                        <img class="w-10 h-10 rounded-full border-2 box-content" :class="req.user ? 'border-white' : ''"
                                            :src="req.user ? req.user.profile_photo_url : '/images/new.png'"
                                        />
                                    </div>

                                    <div class="grow text-left">
                                        <h2 class="font-semibold inline-flex" :class="req.user ? 'text-sky' : 'text-gray-800'">
                                            {{ req.user ? req.user.name : req.requester_name }}
                                        </h2>
                                        <div :class="'text-xs text-gray-500'+ ((req.user || req.full_phone) ? ' font-semibold' : '')">
                                            {{ req.user ? req.user.full_phone : (req.full_phone || 'Numéro non défini') }}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td class="capitalize">{{ formattedDateTime(req.created_at) }}</td>
                            <td>{{ req.weight_package ? req.weight_package : 'Non défini' }}</td>
                            <td><StatusBadge :label="req.status_text" :color="req.status_color" /></td>
                            <td>
                                <div class="space-x-5 flex items-center justify-end">
                                    <i @click.prevent="openDetails(req)" aria-controls="description-02"
                                        class="fad fa-arrow-down text-sm text-sky hover:text-sky-400 transform
                                        cursor-pointer transition-transform duration-200 ease-in-out"
                                        :class="{ 'rotate-180' : selectedRequest && selectedRequest.id === req.id }"
                                        :title="selectedRequest && selectedRequest.id === req.id ? 'Cacher' : 'Détails'"
                                    ></i>

                                    <i v-if="req.status_enum_name === 'pending' && $page.props.permissions.canAddPackage"
                                        @click.prevent="emitter.emit('showFromRequest', req)"
                                        class="fad fa-check-circle text-lg text-greendack-500 hover:text-greendack-400 cursor-pointer"
                                        title="Traiter"
                                    ></i>
                                    <i v-else class="fad fa-check-circle text-lg text-gray-500 opacity-50 cursor-not-allowed"></i>

                                    <i v-if="req.status_enum_name === 'pending' && $page.props.permissions.canAddPackage"
                                        @click.prevent="reject(req)"
                                        class="fad fa-times-circle text-lg text-red-500 hover:text-red-400 cursor-pointer"
                                        title="Rejeter"
                                    ></i>
                                    <i v-else class="fad fa-times-circle text-lg text-gray-500 opacity-50 cursor-not-allowed"></i>
                                </div>
                            </td>
                        </tr>
                            <tr id="description-02" role="region" v-show="selectedRequest && selectedRequest.id === req.id">
                            <td colspan="7">
                                <div class="w-full my-1 bg-gray-100 p-2">
                                    <div class="text-left text-sm px-4 text-gray-700">
                                        <span class="font-bold ">Description du colis:</span> {{ req.description ?? 'Non renseigné' }}
                                    </div>
                                    <hr class="my-2">
                                    <div class="text-left text-sm px-4 text-gray-700">
                                        <span class="font-bold ">Adresse de récupération:</span> {{ req.address ?? 'Non renseigné' }}
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </template>
                </template>
            </Datatables>
        </div>

        <Pagination :links="requests.links" />

        <PackageForm
            v-if="$page.props.permissions.canUpdatePackage || $page.props.permissions.canAddPackage"
            :modes="modes"
            :travel="travel"
            :currency="currency"
        />

        <JetDialogModal :show="searchModal" @close="closeSearchModal" max-width="xl">
            <template #title>Rechercher une demande</template>

            <template #content>
                <GlobalFilter @search-launched="closeSearchModal" />
            </template>
        </JetDialogModal>
    </TravelLayout>
</template>

<script>
    import { defineComponent } from 'vue'
    import { Head } from '@inertiajs/inertia-vue3';
    import Pagination from '@/Components/Pagination'
    import Datatables from '@/Components/Datatables'
    import useDropdown from '@/composables/dropDown'
    import StatusBadge from '@/Components/StatusBadge'
    import PackageForm from '@/Components/PackageForm'
    import JetDialogModal from '@/Jetstream/DialogModal'
    import GlobalFilter from '@/Components/GlobalFilter'
    import TravelLayout from '@/Layouts/TravelLayout.vue'
    import DateFormatMixins from '@/Mixins/DateFormatMixins'

    export default defineComponent({
        components: {
            Head,
            Pagination,
            Datatables,
            StatusBadge,
            PackageForm,
            GlobalFilter,
            TravelLayout,
            JetDialogModal
        },

        mixins: [ DateFormatMixins],

        props: {
            modes: Array,
            travel: Object,
            requests: Object,
            currency: Object
        },

        setup() {
            const { dropdownOpen, trigger, dropdown } = useDropdown()

            return {
                dropdownOpen,
                trigger,
                dropdown
            }
        },

        data() {
            return {
                selectedRequest: null,
                searchModal: false
            }
        },

        methods: {

            showSearchModal () {
                this.searchModal = true
            },

            closeSearchModal() {
                this.searchModal = false
            },

            openDetails(req) {
                if (this.selectedRequest && this.selectedRequest.id === req.id) {
                    this.selectedRequest = null;
                } else {
                    this.selectedRequest = req
                }
            },

            reject(request) {
                this.$swal.queue([{
                    title: "Demande",
                    text: "Êtes vous sûr de vouloir rejeter la demande ?",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: 'rgb(241, 162, 0)',
                    cancelButtonColor: '#bd081c',
                    confirmButtonText: "Rejeter",
                    showLoaderOnConfirm: true,
                    preConfirm: () => {
                        return this.$inertia.put(route('travels.requests.rejected',  {'travel': this.travel, 'request': request}), {
                            preserveScroll: true,
                            preserveState: true,
                            onSuccess: () => {
                                this.$swal.insertQueueStep({
                                    icon: 'success',
                                    title: "Demande",
                                    text: "rejetée avec succès."
                                })
                            },
                            onError: (error) => {
                                this.$swal.insertQueueStep({
                                    icon: 'error',
                                    title: 'Oups, impossible de rejeter la demande.'
                                })
                            }
                        })
                    }
                }])
            }
        }
    })
</script>
